/**
 * The rule preview — the segment builder's live counts.
 *
 *   previewRules   POST /segments/preview   { rules } → people, email/phone reachable, per rule, per group
 *
 * Its own module rather than a line in `segments.ts` because it is not about a
 * segment: nothing is saved, no id is involved, and the Flow builder's condition
 * node asks the same question of the same compiler. What it answers is "who
 * would this rule tree hold right now", computed by the very compiler the
 * refresher runs on save — so the figure under the Save button is the membership
 * the save writes, not an estimate of it.
 *
 * **Debounced by the caller, aborted by the caller.** A builder edit fires one
 * of these per keystroke if nobody stops it; the builder waits for typing to
 * settle and aborts the previous request when a new one starts, so an old
 * answer can never land on top of a newer tree. The `signal` is how.
 *
 * The body is parsed against `segmentPreviewRequestSchema` first. That is not
 * belt-and-braces: the builder holds DRAFT rules (a number box someone has
 * emptied, an `any_of` with nothing ticked), and only the complete ones are
 * sent — a draft that slipped through would be a 400 per keystroke.
 */
import {
  segmentPreviewRequestSchema,
  segmentPreviewResponseSchema,
  type RuleTree,
  type SegmentPreviewResponse,
} from '@wend/shared';

import { apiRequest } from './client';

export async function previewRules(rules: RuleTree, signal?: AbortSignal): Promise<SegmentPreviewResponse> {
  return apiRequest({
    path: '/segments/preview',
    method: 'POST',
    body: segmentPreviewRequestSchema.parse({ rules }),
    schema: segmentPreviewResponseSchema,
    signal,
  });
}
